import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pagination, Progress } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { AppButton } from '../components/common/AppButton';
import { AppDrawer } from '../components/common/AppDrawer';
import { AppLoadError } from '../components/common/AppLoadError';
import { AppModal } from '../components/common/AppModal';
import { DecidedPriceFilterBar } from '../components/decidedPriceFilterBar/DecidedPriceFilterBar';
import { DecidedPriceTable } from '../components/decidedPriceTable/DecidedPriceTable';
import { MarketSellerPanel, type MarketState } from '../components/marketSellerPanel/MarketSellerPanel';
import { useCardPage } from '../contexts/CardPageContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useStorage } from '../contexts/StorageContext';
import { useIsPhone } from '../hooks/UseIsCompact';
import { useContentQuery } from '../hooks/UseContentWidth';
import { useThemeTokens } from '../hooks/UseThemeTokens';
import type { FilterOption } from '../hooks/UseCardView';
import {
  fetchDecisionList, fetchDecisionMarketItems, getJobStatus, saveDecidedPrice, startDecidedPricesBulk,
} from '../api/api';
import type { JobStatus } from '../types/job';
import { CONTENT_BREAKPOINT, FONT_SIZE, FONT_WEIGHT, LAYOUT, LINE_HEIGHT, RADIUS, SPACING } from '../constants/constants';
import { storeLabel } from '../constants/stores';
import type { Card } from '../types/card';
import {
  MAX_DECIDED_BULK, type DecidedPriceRequestItem, type DecidedPriceResult, type DecidedRowResult, type DecisionListItem,
  type DecisionListResponse,
} from '../types/decidedPrice';
import {
  chunk, currentRequestStore, decisionKindOf, hubPriceLabel, hubWarningHelp, hubWarningLabel, isInFlightMessage, loadFavorites, mergeHubWarnings, loadPending, newRequestId, normalizeSaveResult, parsePriceInput,
  pendingKey, pendingStorageFailed,
  planBulk, sameStore, saveErrorOutcome, saveFavorites, updatePending, validateSalePrice,
  type BulkPlan, type DecisionKind, type PendingSave,
} from '../utils/decidedPrice';
import { appMessage as message } from '../utils/appMessage';

/**
 * 판매가 결정(감사 card-controltower#341, 사장 요청 2026-10-29) — 매장별로 카드마다 시장(타 샵) 가격을 보고 판매가를 정해 네이버에
 * 반영하고 PriceHub 에 보고한다. PriceHub 확장팩 화면과 같은 짜임: 위 필터 · 일괄 가격, 왼쪽 표, 오른쪽 판매처 목록(좁은 폭은 아래 시트).
 *
 * 저장 안전장치:
 *   - 요청 번호(requestId)를 카드마다 만들어 보내기 전에 매장별 localStorage 에 먼저 적는다. 네이버 결과가 확실해지면(성공 · 실패) 지우고,
 *     결과 불명(naver.status=UNKNOWN · 응답 없음 · 5xx)이면 남겨 같은 가격 · 같은 requestId 로만 다시 보낸다(새로고침 뒤에도).
 *   - 줄 저장은 카드마다 한 번에 하나, 일괄 저장 중엔 줄 저장을 막는다. 일괄은 200건씩 나눠 보내고 한 묶음이 통째로 실패하면 멈춘다.
 *   - 다른 탭에서 SUPER 매장이 바뀌면(요청이 갈 매장 ≠ 이 화면 매장) 보내지 않는다(#310 과 같은 대비).
 *   - 매장 전환은 MainApp 이 화면을 다시 띄워 모두 비우고, 필터를 바꾸면 선택 · 입력 · 상세를 비운다(#330 규칙).
 */

const PAGE_SIZES = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_KEY = 'decide:pageSize';
const SEARCH_DEBOUNCE_MS = 300;
/**
 * 판매처 패널 · 줄 쌓기는 **본문 폭**으로 정한다(디자인 시스템 v2 템플릿 B · CT 배치 규칙 L3b — 본문 최대 폭 1440 이 생겨 화면 폭 질의가
 * 틀린다): 본문 1000 이상이면 패널을 표 오른쪽에(300), 1400 이상이면 360. v2 의 옆 칸 320 은 표 열 최소 폭 합(684)이 1280 화면 표 자리에
 * 들어가지 않아(4px 넘침) L4(표 열 정리)와 같이 바꾼다. 줄 카드형은 표 열 최소 폭 합(682) + 여백이 안 들어가는 본문
 * 728 미만. 아래 화면 폭 값은 본문을 재기 전(첫 그리기 · 본문 상자 없는 단위 테스트)에만 쓴다.
 */
const SIDE_PANEL_MIN_WIDTH = 1280;
const WIDE_PANEL_MIN_WIDTH = 1600;
const STACK_ROWS_MIN_WIDTH = 760;
const STACK_ROWS_MIN_CONTENT = 728;
const PANEL_WIDTH = 300;
const PANEL_WIDTH_WIDE = 360;

function readPageSize(): number {
  try {
    const v = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZES.includes(v) ? v : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}


/** 다른 탭에서 매장이 바뀌어 목록을 받지 않았다(요청 매장 ≠ 이 화면 매장). */
class StoreMismatchError extends Error {}

const storeChangedMessage = (now: string | null) =>
  `매장이 바뀌어 보내지 않았습니다(지금 요청 매장 ${now ? storeLabel(now) : '미선택'}) — 새로고침해 그 매장 목록을 받은 뒤 다시 하세요.`;

const EMPTY_MARKET: MarketState = { loading: false, error: false, data: null };

/** 매장 가격 요청을 '확정 대기' 탭으로 열어 달라는 한 번짜리 힌트 키(감사 #505) — HubChangesTable 의 같은 이름 상수와 같은 값. */
export const STORE_PRICE_OPEN_TAB_KEY = 'storePrice:openTab';

/** 묶음 잡 폴링 간격(JobProgressBar 와 같은 2초) · 연속 조회 실패 한도(넘으면 결과 불명으로 멈춘다). */
const JOB_POLL_MS = 2_000;
const JOB_POLL_MAX_ERRORS = 5;
/** 서버가 같은 요청을 처리 중(409)이라고 하면 이만큼 다시 시도를 잠근다. */
const BUSY_LOCK_MS = 15_000;

/** 일괄 저장 진행 · 결과(잡 진행 창). */
interface BulkProgress {
  running:   boolean;
  /** 지금 묶음(1부터) / 묶음 수. */
  part:      number;
  parts:     number;
  processed: number;
  total:     number;
  /** 지금 묶음에서 아직 결과가 오지 않은(처리 중) 건수. */
  inFlight:  number;
  phase:     string;
  tally:     { success: number; failed: number; unknown: number; notSent: number };
  /** 반영되지 않은 · 결과 불명 줄(카드 · 사유). */
  problems:  { cardId: number; kind: 'failed' | 'unknown' | 'notSent'; message: string | null }[];
  error:     string | null;
}

const sleep = (ms: number) => new Promise<void>((r) => { setTimeout(r, ms); });

/** 사이드바 메뉴가 띄우는 화면 — 확장팩 · 레어도 칩은 이 매장 카드 목록(CardPageContext)에서 뽑는다. */
export const DecidedPricePage: React.FC = () => {
  const { cards } = useCardPage();
  const { setCards } = cards;
  // 저장 결과를 공통 카드 캐시(스토어 상품 관리 · 매장 가격 요청 · 가격 조회)에도 반영한다(코덱 리뷰 중간 3). 서버 markDecided 와 같은 칸:
  // 수정가 = 결정가 · 수동 · 감지/분류 비움, 네이버 성공이면 기준가(현재가)도.
  const onCardsPatched = useCallback((patches: CardPatch[]) => {
    if (patches.length === 0) return;
    const byId = new Map(patches.map((p) => [p.cardId, p]));
    setCards((prev) => prev.map((c) => {
      const p = byId.get(c.id);
      if (!p) return c;
      const currentPrice = p.success ? p.price : c.currentPrice;
      return {
        ...c,
        suggestedPrice: p.price,
        currentPrice,
        isManual: true,
        manualOverrideAt: null,
        // 결정이 사람이 정한 가격 보호를 대신한다(서버 isPriceHeld() 도 결정 뒤 false — #650).
        priceHeld: false,
        dropBucket: null, dropSeverity: null, dropReason: null,
        marketMin: null, marketMax: null, marketShopCount: null, marketCheckedAt: null,
        ...(p.success ? { priceChangeStatus: 'UNCHANGED' as const, priceChangeRate: 0 } : {}),
      };
    }));
  }, [setCards]);
  return <DecidedPriceScreen cards={cards.cards} onCardsPatched={onCardsPatched} />;
};

/** 저장 결과로 공통 카드 캐시에 덮을 값. */
export interface CardPatch {
  cardId:  number;
  price:   number;
  /** 네이버 반영 성공 — 그때만 기준가(현재가)도 바뀐다. */
  success: boolean;
}

export const DecidedPriceScreen: React.FC<{ cards: Card[]; onCardsPatched?: (p: CardPatch[]) => void }> = ({ cards, onCardsPatched }) => {
  const t = useThemeTokens();
  const { isAdmin, store, superStore } = useStorage();
  const scopeStore = store ?? superStore;
  const readOnly = !isAdmin;
  const phone = useIsPhone();
  const sidePanel = useContentQuery((w) => w >= CONTENT_BREAKPOINT.splitSide, `(min-width: ${SIDE_PANEL_MIN_WIDTH}px)`);
  // 표 열 최소 폭 합(682px)이 본문에 안 들어가는 폭(760 미만, 사이드바는 접힘)에서는 줄을 카드형으로 쌓는다(코덱 리뷰 중간 7).
  // max-width 로 묻는다 — matchMedia 가 없는 환경(테스트)에서는 넓은 화면으로 본다.
  const stackRows = useContentQuery((w) => w < STACK_ROWS_MIN_CONTENT, `(max-width: ${STACK_ROWS_MIN_WIDTH - 1}px)`);
  const widePanel = useContentQuery((w) => w >= CONTENT_BREAKPOINT.splitWide, `(min-width: ${WIDE_PANEL_MIN_WIDTH}px)`);
  // 저장 결과 'PriceHub 확정 대기' 에서 매장 가격 요청 화면의 '확정 대기' 탭으로(세 화면 재설계 F5, 감사 #505 — 기본 탭인
  // '요청 가능' 에는 확정 대기 카드가 없다). 탭은 한 번짜리 sessionStorage 힌트로 넘긴다 — 읽는 쪽은 HubChangesTable
  // (PR #75, 키 STORE_PRICE_OPEN_TAB_KEY 와 같아야 한다). 저장소가 막혀도 화면은 옮긴다(기본 탭).
  const { go } = useNavigation();
  const goStorePrice = useCallback(() => {
    try { sessionStorage.setItem(STORE_PRICE_OPEN_TAB_KEY, 'pending'); } catch { /* 기본 탭으로 */ }
    go('storePrice');
  }, [go]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── 필터 ─────────────────────────────────────────────────────────────────
  // 게임(감사 card-controltower#438) · 레어도 여럿(OR, #448)은 서버가 거른다(백엔드 v0.46.3 decision-list game · rarity 여럿,
  // 감사 #491 — 예전엔 받은 페이지 안에서만 걸러 다른 페이지의 카드가 빠졌다). 확장팩 · 레어도 칩 목록은 이 매장 카드로 만든다.
  const [game, setGame] = useState<string>('ALL');
  const [expansion, setExpansion] = useState('ALL');
  const [searchDraft, setSearchDraft] = useState('');
  const [q, setQ] = useState('');
  const [rarities, setRarities] = useState<string[]>([]);
  const [kinds, setKinds] = useState<DecisionKind[]>([]);
  const [unsetOnly, setUnsetOnly] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState<number>(readPageSize);

  // ── 목록 ─────────────────────────────────────────────────────────────────
  const [list, setList] = useState<DecisionListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [reloadTick, setReloadTick] = useState(0);

  // ── 줄 작업 ──────────────────────────────────────────────────────────────
  const [drafts, setDrafts] = useState<Map<number, string>>(new Map());
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [detailId, setDetailId] = useState<number | null>(null);
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const savingRef = useRef<Set<number>>(new Set());
  const [results, setResults] = useState<Map<number, DecidedRowResult>>(new Map());
  const [pending, setPending] = useState<Map<number, PendingSave>>(() => loadPending(scopeStore));
  const [favorites, setFavorites] = useState<Set<number>>(() => loadFavorites(scopeStore));

  // ── 일괄 ─────────────────────────────────────────────────────────────────
  const [bulkPrice, setBulkPrice] = useState('');
  const [belowOnly, setBelowOnly] = useState(false);
  const [bulkPlan, setBulkPlan] = useState<BulkPlan | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const bulkRunningRef = useRef(false);
  const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);
  // 서버가 같은 요청을 처리 중(409)이라 잠시 다시 시도를 잠근 카드.
  const [busy, setBusy] = useState<Set<number>>(new Set());
  // "스토어 확인 후 새로 결정" 확인 창 — 결과 불명 요청을 버리고 새 요청 번호로(Opus 리뷰 높음 1).
  const [resetTarget, setResetTarget] = useState<{ cardId: number; price: number; requestId: string } | null>(null);
  // PriceHub 경고가 있는 카드의 저장 전 확인(감사 card-controltower#362 — 막지 않고 한 번 확인).
  const [warnTarget, setWarnTarget] = useState<number | null>(null);

  // ── 판매처 목록 ──────────────────────────────────────────────────────────
  const [market, setMarket] = useState<MarketState>(EMPTY_MARKET);
  const [marketTick, setMarketTick] = useState(0);

  const refreshPending = useCallback(() => {
    if (mountedRef.current) setPending(loadPending(scopeStore));
  }, [scopeStore]);

  // 다른 탭에서 같은 매장의 결과 불명 보관이 바뀌면 따라간다.
  useEffect(() => {
    const key = pendingKey(scopeStore);
    // 카드별 키(<머리>:<id>) · 옛 한 키 · 전체 비우기(key=null) 모두.
    const onStorage = (e: StorageEvent) => { if (e.key == null || e.key === key || e.key.startsWith(`${key}:`)) refreshPending(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [scopeStore, refreshPending]);

  // ── 목록 받기 ────────────────────────────────────────────────────────────
  // 저장으로 줄을 고친 뒤 그보다 먼저 보낸 목록 조회가 늦게 오면 옛 값으로 덮는다 — 순번으로 비교해 고친 값을 다시 얹는다(코덱 리뷰 중간 6).
  const clockRef = useRef(0);
  const rowPatchesRef = useRef<Map<number, { at: number; apply: (i: DecisionListItem) => DecisionListItem }>>(new Map());
  useEffect(() => {
    const now = currentRequestStore();
    if (!sameStore(now, scopeStore)) {
      setLoading(false);
      setLoadError(new StoreMismatchError(storeChangedMessage(now)));
      return undefined;
    }
    const ac = new AbortController();
    const startedAt = ++clockRef.current;
    setLoading(true);
    fetchDecisionList({
      expansion: expansion === 'ALL' ? undefined : expansion,
      q,
      game: game === 'ALL' ? undefined : game,
      rarities,
      unsetOnly,
      page,
      size,
    }, { signal: ac.signal })
      .then((r) => {
        if (ac.signal.aborted) return;
        const patches = rowPatchesRef.current;
        const items = r.items.map((i) => {
          const p = patches.get(i.cardId);
          return p && p.at > startedAt ? p.apply(i) : i;
        });
        setList({ ...r, items });
        setLoadError(null);
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setLoadError(e);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [scopeStore, expansion, q, game, rarities, unsetOnly, page, size, reloadTick]);

  // 저장 · 다른 탭 변경으로 목록이 줄어 지금 페이지가 비면 마지막 페이지로 당긴다.
  useEffect(() => {
    if (!list || list.items.length > 0 || page === 0 || list.total === 0) return;
    setPage(Math.max(0, Math.ceil(list.total / size) - 1));
  }, [list, page, size]);

  // ── 판매처 목록 받기(고른 카드) ──────────────────────────────────────────
  useEffect(() => {
    if (detailId == null) { setMarket(EMPTY_MARKET); return undefined; }
    const ac = new AbortController();
    setMarket({ loading: true, error: false, data: null });
    fetchDecisionMarketItems(detailId, { signal: ac.signal })
      .then((d) => { if (!ac.signal.aborted) setMarket({ loading: false, error: false, data: d }); })
      .catch(() => { if (!ac.signal.aborted) setMarket({ loading: false, error: true, data: null }); });
    return () => ac.abort();
  }, [detailId, marketTick]);

  // ── 필터 바꾸기 — 선택 · 입력 · 상세를 비우고 1페이지로(#330 규칙) ──────────
  const resetWork = useCallback(() => {
    setSelected(new Set());
    setDrafts(new Map());
    setDetailId(null);
  }, []);

  const changeFilter = useCallback((apply: () => void) => {
    apply();
    setPage(0);
    resetWork();
  }, [resetWork]);

  const qRef = useRef(q);
  qRef.current = q;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);
  const onSearch = useCallback((v: string) => {
    setSearchDraft(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      const next = v.trim();
      if (next === qRef.current) return;
      changeFilter(() => setQ(next));
    }, SEARCH_DEBOUNCE_MS);
  }, [changeFilter]);

  const goPage = (p: number, s: number) => {
    if (s !== size) {
      setSize(s);
      try { localStorage.setItem(PAGE_SIZE_KEY, String(s)); } catch { /* 이번 화면만 */ }
      setPage(0);
    } else {
      setPage(p);
    }
    setSelected(new Set());
    setDetailId(null);
  };

  // ── 표시할 줄(구분 · 즐겨찾기는 받은 페이지 안에서) ──────────────────────────
  const items = useMemo(() => list?.items ?? [], [list]);
  // 재고(감사 #447) — 매장 가격 요청 재고 칸(#348)과 같은 출처(CT 카드 stockQuantity).
  const stockById = useMemo(() => new Map(cards.map((c) => [c.id, c.stockQuantity])), [cards]);
  const visible = useMemo(() => items.filter((it) =>
    (kinds.length === 0 || kinds.includes(decisionKindOf(it)))
    && (!favOnly || favorites.has(it.cardId))),
  [items, kinds, favOnly, favorites]);
  const clientFiltered = kinds.length > 0 || favOnly;
  const detailItem = useMemo(() => items.find((i) => i.cardId === detailId) ?? null, [items, detailId]);

  // 사람이 정한 가격 보호(감사 #538, 백엔드 PR #77) — 목록 응답의 priceHeld(서버 값)를 따른다. 옛 서버(칸 없음)만 카드 캐시에서 싣는다
  // (클라우드 리뷰 중간 #650 — 캐시의 true 로 덮으면 결정 뒤 서버는 보호가 풀렸는데 카드를 다시 받을 때까지 '유지 중' 이 남았다).
  const tableItems = useMemo(() => {
    const held = new Set(cards.filter((c) => c.priceHeld).map((c) => c.id));
    return held.size === 0 ? visible
      : visible.map((it) => (typeof it.priceHeld !== 'boolean' && held.has(it.cardId) ? { ...it, priceHeld: true } : it));
  }, [cards, visible]);

  const expansionOptions = useMemo<FilterOption[]>(() => {
    const byCode = new Map<string, { label: string; count: number }>();
    for (const c of cards) {
      if (!c.expansionCode) continue;
      if (game !== 'ALL' && c.cardType !== game) continue;
      const cur = byCode.get(c.expansionCode);
      if (cur) cur.count += 1;
      else byCode.set(c.expansionCode, { label: c.expansionName ?? c.expansionCode, count: 1 });
    }
    return Array.from(byCode, ([value, { label, count }]) => ({ value, label, count }));
  }, [cards, game]);

  const rarityOptions = useMemo<FilterOption[]>(() => {
    const set = new Set<string>();
    for (const c of cards) {
      if (game !== 'ALL' && c.cardType !== game) continue;
      if (expansion !== 'ALL' && c.expansionCode !== expansion) continue;
      if (c.rarity) set.add(c.rarity);
    }
    rarities.forEach((r) => set.add(r));
    return Array.from(set).sort().map((r) => ({ value: r, label: r }));
  }, [cards, game, expansion, rarities]);

  // ── 줄 상태 고치기 ───────────────────────────────────────────────────────
  const onDraft = useCallback((id: number, raw: string) => {
    const d = raw.replace(/[^0-9]/g, '');
    setDrafts((m) => {
      const n = new Map(m);
      if (d) n.set(id, d); else n.delete(id);
      return n;
    });
  }, []);

  const onToggleSelect = useCallback((id: number) => setSelected((cur) => {
    const n = new Set(cur);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }), []);

  const onToggleAll = useCallback(() => setSelected((cur) => {
    const all = visible.length > 0 && visible.every((i) => cur.has(i.cardId));
    return all ? new Set() : new Set(visible.map((i) => i.cardId));
  }), [visible]);

  const onToggleFavorite = useCallback((id: number) => setFavorites((cur) => {
    const n = new Set(cur);
    if (n.has(id)) n.delete(id); else n.add(id);
    saveFavorites(scopeStore, n);
    return n;
  }), [scopeStore]);

  const onOpenDetail = useCallback((id: number) => setDetailId(id), []);

  const setRowResult = useCallback((id: number, r: DecidedRowResult) => {
    if (!mountedRef.current) return;
    setResults((m) => new Map(m).set(id, r));
  }, []);

  const markSaving = useCallback((ids: number[], on: boolean) => {
    ids.forEach((id) => (on ? savingRef.current.add(id) : savingRef.current.delete(id)));
    if (mountedRef.current) setSaving(new Set(savingRef.current));
  }, []);

  /** 같은 요청 번호일 때만 결과 불명 보관을 지운다 — 그 사이 다른 탭 · 다른 저장이 새로 적은 것을 지우지 않게. */
  const clearPendingIf = useCallback((forStore: string | null, id: number, requestId: string) => {
    updatePending(forStore, (m) => { if (m.get(id)?.requestId === requestId) m.delete(id); });
  }, []);

  const markBusy = useCallback((ids: number[]) => {
    if (!mountedRef.current || ids.length === 0) return;
    setBusy((cur) => new Set([...Array.from(cur), ...ids]));
    setTimeout(() => {
      if (!mountedRef.current) return;
      setBusy((cur) => {
        const n = new Set(cur);
        ids.forEach((id) => n.delete(id));
        return n;
      });
    }, BUSY_LOCK_MS);
  }, []);

  /** 저장 응답 한 건을 줄에 반영한다. */
  const applyOutcome = useCallback((
    forStore: string | null, sent: DecidedPriceRequestItem, r: DecidedPriceResult,
  ): DecidedRowResult['kind'] => {
    // 묶음 decisions 의 '같은 요청 처리 중' 은 FAILED + 그 문구로 온다 — 실패가 아니라 처리 중(Opus 리뷰 중간 2).
    const inFlight = r.naver.status === 'FAILED' && isInFlightMessage(r.naver.message);
    const kind: DecidedRowResult['kind'] = inFlight ? 'busy'
      : r.naver.status === 'SUCCESS' ? 'success' : r.naver.status === 'FAILED' ? 'failed' : 'unknown';
    if (kind === 'success' || kind === 'failed') clearPendingIf(forStore, sent.cardId, sent.requestId);
    if (kind === 'busy') markBusy([sent.cardId]);
    // 서버는 네이버 PUT 전에 결정가 · 결정 시각 · 수동 표시를 남긴다 — 실패 · 결과 불명이어도 줄을 '결정됨 · 수동' 으로(Opus 리뷰 중간 5).
    // 네이버 판매가(currentPrice)는 성공일 때만 바뀐다. 카드를 못 찾은 경우 · 처리 중은 서버가 손대지 않았다. 새 서버(#501)는
    // 결정을 남기기 전에 끝난 실패에 decidedAt=null 을 준다 — 그때도 손대지 않은 것.
    const touched = kind !== 'busy' && r.decidedAt !== null
      && !(kind === 'failed' && /카드를 찾을 수 없음/.test(r.naver.message ?? ''));
    // 결정 시각은 서버 값(#501), 옛 서버면 화면 시각.
    const decidedAt = touched ? (r.decidedAt ?? new Date().toISOString()) : undefined;
    setRowResult(sent.cardId, {
      kind, price: r.price, message: r.naver.message, hub: r.hub, hubWarnings: r.hubWarnings, decidedAt,
    });
    if (touched && mountedRef.current) {
      // 경고는 합친다 — 같은 requestId 재전송 결과는 [] 로 와 목록 값을 지우면 안 된다(#362).
      const apply = (i: DecisionListItem): DecisionListItem => ({
        ...i, currentPrice: kind === 'success' ? r.price : i.currentPrice, decidedAt: decidedAt ?? i.decidedAt, isManual: true, priceHeld: false,
        hubWarnings: mergeHubWarnings(i.hubWarnings, r.hubWarnings),
      });
      rowPatchesRef.current.set(sent.cardId, { at: ++clockRef.current, apply });
      setList((cur) => (cur ? {
        ...cur,
        items: cur.items.map((i) => (i.cardId === sent.cardId ? apply(i) : i)),
      } : cur));
    }
    if (touched) onCardsPatched?.([{ cardId: sent.cardId, price: r.price, success: kind === 'success' }]);
    if (kind === 'success' && mountedRef.current) {
      setDrafts((m) => {
        if (!m.has(sent.cardId)) return m;
        const n = new Map(m);
        n.delete(sent.cardId);
        return n;
      });
    }
    return kind;
  }, [clearPendingIf, setRowResult, markBusy, onCardsPatched]);

  // ── 한 건 저장 ───────────────────────────────────────────────────────────
  const saveOne = useCallback(async (id: number, opts: { warned?: boolean } = {}) => {
    // 줄 저장은 한 번에 하나 — 백엔드 매장 락(DECIDED_PRICE)이 겹치면 둘째를 바로 409 로 거절한다(Opus 리뷰 중간 3).
    if (readOnly || savingRef.current.size > 0 || bulkRunningRef.current || busy.has(id)) return;
    const now = currentRequestStore();
    if (!sameStore(now, scopeStore)) {
      message.warning(storeChangedMessage(now));
      return;
    }
    const forStore = scopeStore;
    const prev = loadPending(forStore).get(id);
    // PriceHub 경고가 있으면 새 결정 전에 한 번 확인(결과 불명 다시 시도는 이미 확인한 같은 요청이라 묻지 않는다).
    if (!prev && !opts.warned && (items.find((i) => i.cardId === id)?.hubWarnings?.length ?? 0) > 0) {
      const err = validateSalePrice(parsePriceInput(draftsRef.current.get(id)));
      if (err) message.warning(err); else setWarnTarget(id);
      return;
    }
    let sent: DecidedPriceRequestItem;
    if (prev) {
      // 결과 불명 — 같은 가격 · 같은 요청 번호로만.
      sent = { cardId: id, price: prev.price, requestId: prev.requestId };
    } else {
      const price = parsePriceInput(draftsRef.current.get(id));
      const err = validateSalePrice(price);
      if (err) {
        message.warning(err);
        return;
      }
      sent = { cardId: id, price: price as number, requestId: newRequestId() };
      // 보내기 전에 먼저 적는다 — 응답 전에 창이 닫혀도 다음에 같은 요청 번호로만 다시 보낸다.
      updatePending(forStore, (m) => m.set(id, { price: sent.price, requestId: sent.requestId, at: new Date().toISOString() }));
    }
    markSaving([id], true);
    refreshPending();
    try {
      const r = await saveDecidedPrice(id, { price: sent.price, requestId: sent.requestId });
      const kind = applyOutcome(forStore, sent, r);
      if (kind === 'failed') message.warning(`네이버 반영 실패${r.naver.message ? ` — ${r.naver.message}` : ''}`);
      else if (kind === 'unknown') message.warning('네이버 반영 결과를 알 수 없습니다 — "다시 시도" 는 같은 요청으로 보냅니다');
    } catch (e) {
      // 결과 불명 요청을 같은 번호로 다시 보낸 경우의 409 는 '처리 중' — 번호를 지우지 않고 잠시 잠근다.
      const o = saveErrorOutcome(e, { retry: !!prev });
      if (o.kind === 'failed') clearPendingIf(forStore, id, sent.requestId);
      if (o.kind === 'busy') markBusy([id]);
      setRowResult(id, { kind: o.kind, price: sent.price, message: o.message, hub: null });
      message.warning(o.message);
    } finally {
      markSaving([id], false);
      refreshPending();
    }
  }, [readOnly, scopeStore, busy, items, markSaving, markBusy, refreshPending, applyOutcome, clearPendingIf, setRowResult]);

  // ── 여러 건 저장(일괄 · 결과 불명 다시 시도) ─────────────────────────────
  //
  // 백엔드(#340)는 묶음을 잡으로 돌리고 {jobId} 만 준다 — GET /api/cards/jobs/{jobId} 를 2초마다 보고, decisions(처리된 항목만 ·
  // 요청 순서로 쌓인다, e3aff15)가 올라올 때마다 그 줄에 결과를 적는다 — 아직 없는 줄은 '처리 중'(저장 중 표시). 잡이 실패 · 사라짐 · 조회가 계속 안 되면 결과가 없는 카드는 결과 불명(같은 요청 번호로만 다시)으로 두고 멈춘다.
  // 화면을 떠나도 서버 잡은 계속 돈다 — 보관된 요청 번호로 다시 보내면 서버가 첫 결과를 돌려준다(멱등).

  /** 잡이 끝날 때까지 본다. 화면을 떠나면 null. */
  const pollJob = useCallback(async (
    jobId: string, onTick: (j: JobStatus) => void,
  ): Promise<{ job: JobStatus | null; lost: string | null }> => {
    let errors = 0;
    let last: JobStatus | null = null;
    for (let first = true; ; first = false) {
      if (!first) await sleep(JOB_POLL_MS);
      if (!mountedRef.current) return { job: last, lost: '화면을 떠나 결과를 받지 못했습니다' };
      try {
        const j = await getJobStatus(jobId);
        errors = 0;
        last = j;
        onTick(j);
        if (j.status !== 'RUNNING') return { job: j, lost: null };
      } catch (e) {
        if ((e as { response?: { status?: number } })?.response?.status === 404) {
          return { job: last, lost: '서버에서 작업 정보를 찾을 수 없습니다' };
        }
        errors += 1;
        if (errors >= JOB_POLL_MAX_ERRORS) return { job: last, lost: '작업 상태를 받지 못했습니다' };
      }
    }
  }, []);

  const runBulk = useCallback(async (targets: DecidedPriceRequestItem[]) => {
    if (readOnly || bulkRunningRef.current || targets.length === 0) return;
    if (savingRef.current.size > 0) {
      message.warning('다른 카드를 저장하는 중입니다 — 끝난 뒤 다시 하세요');
      return;
    }
    const now = currentRequestStore();
    if (!sameStore(now, scopeStore)) {
      message.warning(storeChangedMessage(now));
      return;
    }
    const forStore = scopeStore;
    bulkRunningRef.current = true;
    setBulkRunning(true);
    setBulkPlan(null);
    const parts = chunk(targets, MAX_DECIDED_BULK);
    const progress: BulkProgress = {
      running: true, part: 0, parts: parts.length, processed: 0, total: targets.length, inFlight: 0, phase: '',
      tally: { success: 0, failed: 0, unknown: 0, notSent: 0 }, problems: [], error: null,
    };
    const push = () => { if (mountedRef.current) setBulkProgress({ ...progress, tally: { ...progress.tally }, problems: [...progress.problems] }); };
    const notSent = (list: DecidedPriceRequestItem[], why: string) => {
      progress.tally.notSent += list.length;
      list.forEach((it) => progress.problems.push({ cardId: it.cardId, kind: 'notSent', message: why }));
    };
    push();
    let doneBefore = 0;
    try {
      for (let pi = 0; pi < parts.length; pi += 1) {
        progress.part = pi + 1;
        if (!mountedRef.current || !sameStore(currentRequestStore(), forStore)) {
          notSent(parts.slice(pi).flat(), '매장이 바뀌거나 화면을 떠나 보내지 않음');
          break;
        }
        // 먼저 적기 — 이미 다른 요청 번호로 결과 불명인 카드 · 지금 줄 저장 중 · 서버 처리 중(409)인 카드는 이번에 보내지 않는다.
        let sendable: DecidedPriceRequestItem[] = [];
        const retried = new Set<number>();
        updatePending(forStore, (m) => {
          sendable = parts[pi].filter((it) => {
            if (savingRef.current.has(it.cardId) || busy.has(it.cardId)) return false;
            const p = m.get(it.cardId);
            return !p || p.requestId === it.requestId;
          });
          const at = new Date().toISOString();
          sendable.forEach((it) => {
            if (m.has(it.cardId)) retried.add(it.cardId);
            else m.set(it.cardId, { price: it.price, requestId: it.requestId, at });
          });
        });
        const sendIds = new Set(sendable.map((it) => it.cardId));
        notSent(parts[pi].filter((it) => !sendIds.has(it.cardId)), '다른 요청이 진행 · 결과 불명 중');
        if (sendable.length === 0) continue;
        const ids = sendable.map((it) => it.cardId);
        markSaving(ids, true);
        refreshPending();
        push();
        let stop = false;
        try {
          let jobId: string;
          try {
            ({ jobId } = await startDecidedPricesBulk(sendable));
          } catch (e) {
            // 잡 시작 자체가 실패 — 4xx 는 받지 않음(새 요청 가능), 응답 없음 · 5xx 는 결과 불명. 다시 보낸 요청 번호의 409 는 처리 중.
            let lastMessage = '';
            const busyIds: number[] = [];
            for (const it of sendable) {
              const o = saveErrorOutcome(e, { retry: retried.has(it.cardId) });
              lastMessage = o.message;
              if (o.kind === 'failed') clearPendingIf(forStore, it.cardId, it.requestId);
              if (o.kind === 'busy') busyIds.push(it.cardId);
              setRowResult(it.cardId, { kind: o.kind, price: it.price, message: o.message, hub: null });
              const k = o.kind === 'failed' ? 'failed' : 'unknown';
              progress.problems.push({ cardId: it.cardId, kind: k, message: o.message });
              progress.tally[k] += 1;
            }
            markBusy(busyIds);
            progress.error = lastMessage;
            notSent(parts.slice(pi + 1).flat(), '앞 묶음이 실패해 멈춤');
            stop = true;
            // continue 면 아래 중단 검사를 건너뛰어 다음 묶음을 보냈다(코덱 리뷰 중간 4) — finally 뒤 바로 멈춘다.
            break;
          }
          // 진행률은 요청한 건수(sendable)를 기준으로 — decisions 는 처리된 항목만 쌓인다(e3aff15).
          const base = doneBefore;
          const sentById = new Map(sendable.map((it) => [it.cardId, it]));
          const applied = new Set<number>();
          const record = (it: DecidedPriceRequestItem, r: DecidedPriceResult) => {
            applied.add(it.cardId);
            const kind = applyOutcome(forStore, it, r);
            const k = kind === 'busy' ? 'unknown' : kind;
            progress.tally[k] += 1;
            if (k !== 'success') progress.problems.push({ cardId: it.cardId, kind: k, message: r.naver.message });
            markSaving([it.cardId], false);
          };
          const take = (decisions: unknown[] | null | undefined) => {
            for (const d of decisions ?? []) {
              const id = (d as { cardId?: unknown })?.cardId;
              if (typeof id !== 'number' || applied.has(id)) continue;
              const it = sentById.get(id);
              if (it) record(it, normalizeSaveResult(d, it));
            }
            progress.processed = base + applied.size;
            progress.inFlight = sendable.length - applied.size;
          };
          progress.inFlight = sendable.length;
          push();
          const { job, lost } = await pollJob(jobId, (j) => {
            take(j.decisions);
            progress.phase = j.phase ?? '';
            push();
          });
          take(job?.decisions);
          // 끝까지 결과가 오지 않은 카드는 결과 불명 — 요청 번호를 남겨 같은 번호로만 다시 보낸다.
          for (const it of sendable) {
            if (applied.has(it.cardId)) continue;
            record(it, {
              cardId: it.cardId, price: it.price, hub: null,
              naver: { status: 'UNKNOWN', message: lost ?? job?.error ?? '응답에 이 카드 결과가 없습니다' },
            });
          }
          progress.inFlight = 0;
          doneBefore += sendable.length;
          progress.processed = doneBefore;
          if (lost || job?.status === 'FAILED') {
            progress.error = lost ?? job?.error ?? '작업이 실패했습니다';
            notSent(parts.slice(pi + 1).flat(), '앞 묶음이 실패해 멈춤');
            stop = true;
          }
        } finally {
          markSaving(ids, false);
          push();
        }
        if (stop) break;
      }
    } finally {
      bulkRunningRef.current = false;
      progress.running = false;
      push();
      if (mountedRef.current) {
        setBulkRunning(false);
        setSelected(new Set());
      }
      refreshPending();
    }
  }, [readOnly, scopeStore, busy, markSaving, markBusy, refreshPending, applyOutcome, clearPendingIf, setRowResult, pollJob]);

  const openBulk = () => {
    const price = parsePriceInput(bulkPrice);
    const err = validateSalePrice(price);
    if (err) {
      message.warning(err);
      return;
    }
    const plan = planBulk(visible, selected, price as number, belowOnly, loadPending(scopeStore));
    if (plan.targets.length === 0) {
      const why = [
        plan.excluded.notBelow ? `네이버 가격이 일괄 가격 이상 ${plan.excluded.notBelow}건` : null,
        plan.excluded.same ? `이미 같은 가격 ${plan.excluded.same}건` : null,
        plan.excluded.pending ? `결과 불명 ${plan.excluded.pending}건` : null,
      ].filter(Boolean).join(' · ');
      message.info(`적용할 카드가 없습니다${why ? ` — ${why}` : ''}`);
      return;
    }
    setBulkPlan(plan);
  };

  // 결과 불명 띠 — 지금 보내는 중인 줄(먼저 적어 둔 번호)은 빼고 센다(Opus 리뷰 중간 4).
  const pendingList = useMemo(
    () => Array.from(pending.entries()).filter(([id]) => !saving.has(id)),
    [pending, saving],
  );
  const anySaving = saving.size > 0;
  const saveLock = bulkRunning ? '일괄 저장이 끝난 뒤 저장할 수 있습니다'
    : anySaving ? '다른 카드를 저장하는 중입니다 — 끝난 뒤 저장하세요' : undefined;

  const onResetPending = useCallback((id: number) => {
    const p = loadPending(scopeStore).get(id);
    if (p) setResetTarget({ cardId: id, price: p.price, requestId: p.requestId });
  }, [scopeStore]);

  const confirmReset = () => {
    const tgt = resetTarget;
    setResetTarget(null);
    if (!tgt || savingRef.current.size > 0 || bulkRunningRef.current) return;
    clearPendingIf(scopeStore, tgt.cardId, tgt.requestId);
    setResults((m) => {
      const n = new Map(m);
      n.delete(tgt.cardId);
      return n;
    });
    setDrafts((m) => new Map(m).set(tgt.cardId, String(tgt.price)));
    refreshPending();
  };
  const retryAllPending = () => runBulk(pendingList.map(([cardId, p]) => ({ cardId, price: p.price, requestId: p.requestId })));

  const selectedCount = useMemo(() => visible.filter((i) => selected.has(i.cardId)).length, [visible, selected]);
  const bulkDisabledReason = readOnly ? '보기 권한 계정은 저장할 수 없습니다'
    : bulkRunning ? '일괄 저장 중입니다'
      : anySaving ? '다른 카드를 저장하는 중입니다'
        : selectedCount === 0 ? '카드를 먼저 체크하세요'
        : !bulkPrice ? '일괄 가격을 입력하세요' : undefined;

  // ── 그리기 ───────────────────────────────────────────────────────────────
  const total = list?.total ?? 0;
  const from = total === 0 ? 0 : page * size + 1;
  const to = Math.min((page + 1) * size, total);
  const failedEmpty = !!loadError && !list;
  // 값이 없으면 출처도 없다 — '(출처 없음)' 꼬리는 값이 있는데 출처를 모를 때만(코덱 PR #74).
  const hubLabel = detailItem?.hubPrice == null ? 'PriceHub 가격' : hubPriceLabel(detailItem.hubPriceSource, scopeStore);
  // 결과 칩의 "매장 가격 요청에서 보기"(세 화면 재설계 F5) — 저장 중에는 화면을 떠나지 않게 링크를 숨긴다.
  const goRequests = bulkRunning || anySaving ? undefined : goStorePrice;
  const loadErrorMessage = loadError instanceof StoreMismatchError ? loadError.message : undefined;

  const panel = detailItem ? (
    <MarketSellerPanel
      item={detailItem}
      market={market}
      hubLabel={hubLabel}
      draft={drafts.get(detailItem.cardId)}
      pending={pending.get(detailItem.cardId)}
      result={results.get(detailItem.cardId)}
      saving={saving.has(detailItem.cardId)}
      readOnly={readOnly}
      saveLock={saveLock}
      busy={busy.has(detailItem.cardId)}
      phone={phone}
      onPick={(price) => onDraft(detailItem.cardId, String(price))}
      onDraft={(raw) => onDraft(detailItem.cardId, raw)}
      onSave={() => { void saveOne(detailItem.cardId); }}
      onRetryMarket={() => setMarketTick((n) => n + 1)}
      onResetPending={() => onResetPending(detailItem.cardId)}
      onGoRequests={goRequests}
    />
  ) : null;

  const note = (text: React.ReactNode, tone: 'info' | 'warning' = 'info', action?: React.ReactNode) => (
    <div
      role={tone === 'warning' ? 'alert' : 'status'}
      style={{
        display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap', padding: `${SPACING.xxs}px ${SPACING.sm}px`,
        borderRadius: RADIUS.md, fontSize: FONT_SIZE.xs, lineHeight: LINE_HEIGHT.relaxed,
        background: tone === 'warning' ? t.tintWarning : t.tintInfo,
        color: tone === 'warning' ? t.warning : t.textSecondary,
        border: `1px solid ${tone === 'warning' ? t.lineWarning : t.borderSubtle}`,
      }}
    >
      <span>{text}</span>
      {action}
    </div>
  );

  return (
    <DashboardLayout title="판매가 결정">
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
        <DecidedPriceFilterBar
          phone={phone}
          expansion={expansion}
          game={game}
          onGame={(v) => changeFilter(() => { setGame(v); setExpansion('ALL'); setRarities([]); })}
          expansionOptions={expansionOptions}
          onExpansion={(v) => changeFilter(() => { setExpansion(v); setRarities([]); })}
          search={searchDraft}
          onSearch={onSearch}
          unsetOnly={unsetOnly}
          favOnly={favOnly}
          favoriteCount={favorites.size}
          onView={(v) => changeFilter(() => { setUnsetOnly(v.unsetOnly); setFavOnly(v.favOnly); })}
          rarities={rarities}
          rarityOptions={rarityOptions}
          onRarities={(v) => changeFilter(() => setRarities(v))}
          kinds={kinds}
          onKinds={(v) => changeFilter(() => setKinds(v))}
          bulkPrice={bulkPrice}
          onBulkPrice={(v) => setBulkPrice(v.replace(/[^0-9]/g, ''))}
          belowOnly={belowOnly}
          onBelowOnly={setBelowOnly}
          selectedCount={selectedCount}
          bulkDisabledReason={bulkDisabledReason}
          bulkRunning={bulkRunning}
          onBulkApply={openBulk}
        />

        {pendingStorageFailed(scopeStore) && note(
          '이 브라우저에 요청 번호를 남기지 못했습니다(저장소 가득 참 · 막힘) — 이 탭에서는 결과 불명 잠금이 유지되지만, 새로고침하거나 탭을 닫으면 사라질 수 있습니다. 결과 불명 카드는 이 탭에서 다시 시도하세요.',
          'warning',
        )}
        {pendingList.length > 0 && note(
          <>결과 불명 <b>{pendingList.length}건</b> — 네이버 반영 여부를 모릅니다. 다시 시도는 같은 가격 · 같은 요청 번호로 보내 두 번 반영되지 않습니다.</>,
          'warning',
          <AppButton size={phone ? 'lg' : 'sm'} disabled={readOnly || bulkRunning || anySaving} onClick={() => { void retryAllPending(); }}>
            {pendingList.length}건 같은 요청으로 다시 시도
          </AppButton>,
        )}
        {clientFiltered && list && total > items.length && note(
          `구분 · 즐겨찾기는 지금 받은 ${items.length}건(${from}~${to}) 안에서만 거릅니다 — 다른 페이지는 넘기거나 확장팩을 고르세요.`,
        )}

        {failedEmpty ? (
          <AppLoadError error={loadError} message={loadErrorMessage}
            onRetry={() => setReloadTick((n) => n + 1)} />
        ) : (
          <div style={{ display: 'flex', gap: SPACING.sm, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: SPACING.xs }}>
              {loadError != null && (
                <AppLoadError inline error={loadError} message={loadErrorMessage}
                  onRetry={() => setReloadTick((n) => n + 1)} />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
                <span>
                  {list ? <>전체 <b style={{ color: t.textPrimary }}>{total.toLocaleString()}</b>장 · {from}~{to}</> : '불러오는 중…'}
                  {clientFiltered && list ? ` · 이 페이지에서 ${visible.length}건` : ''}
                </span>
                <AppButton size="sm" variant="text" icon={<ReloadOutlined />} loading={loading && !!list}
                  style={phone ? { height: 44, minWidth: 44 } : undefined /* 휴대폰 누르는 곳 44px(감사 #518) */}
                  onClick={() => setReloadTick((n) => n + 1)} aria-label="목록 다시 받기">
                  {phone ? '' : '다시 받기'}
                </AppButton>
              </div>
              {list && visible.length === 0 ? (
                <div style={{
                  padding: SPACING.xl, textAlign: 'center', color: t.textMuted, fontSize: FONT_SIZE.sm,
                  border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, background: t.bgSurface,
                }}>
                  {clientFiltered && items.length > 0 ? '이 페이지에 구분 · 즐겨찾기에 맞는 카드가 없습니다' : '조건에 맞는 카드가 없습니다'}
                </div>
              ) : list ? (
                <DecidedPriceTable
                  items={tableItems}
                  phone={phone || stackRows}
                  readOnly={readOnly}
                  drafts={drafts}
                  selected={selected}
                  detailId={detailId}
                  saving={saving}
                  results={results}
                  pending={pending}
                  favorites={favorites}
                  stockById={stockById}
                  storeCode={scopeStore}
                  onGoRequests={goRequests}
                  saveLock={saveLock}
                  busy={busy}
                  onToggleSelect={onToggleSelect}
                  onToggleAll={onToggleAll}
                  onDraft={onDraft}
                  onSave={(id) => { void saveOne(id); }}
                  onOpenDetail={onOpenDetail}
                  onToggleFavorite={onToggleFavorite}
                  onResetPending={onResetPending}
                />
              ) : null}
              {list && total > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Pagination
                    // 휴대폰 폭의 앞 · 뒤 · 페이지 입력 · 크기 선택 44px — TablePager 와 같은 CSS(index.css .table-pager, 감사 #518).
                    className="table-pager"
                    size={phone ? 'middle' : 'small'}
                    current={page + 1}
                    pageSize={size}
                    total={total}
                    showSizeChanger
                    pageSizeOptions={PAGE_SIZES}
                    simple={phone}
                    onChange={(p, s) => goPage(p - 1, s)}
                  />
                </div>
              )}
            </div>

            {sidePanel && (
              <aside
                aria-label="판매처 패널"
                style={{
                  width: widePanel ? PANEL_WIDTH_WIDE : PANEL_WIDTH, flexShrink: 0, position: 'sticky', top: LAYOUT.headerHeight + SPACING.md,
                  maxHeight: `calc(100vh - ${LAYOUT.headerHeight + SPACING.md * 2}px)`, overflowY: 'auto',
                  background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, padding: SPACING.sm,
                }}
              >
                {panel ?? (
                  <div style={{ color: t.textMuted, fontSize: FONT_SIZE.sm, lineHeight: LINE_HEIGHT.relaxed }}>
                    <div style={{ fontWeight: FONT_WEIGHT.semibold, color: t.textSecondary }}>판매처 목록</div>
                    왼쪽 표에서 카드를 누르면 이미지 · 수집일시 · 판매처별 가격이 여기 보입니다. 판매처를 누르면 그 가격이 입력칸에 들어갑니다.
                  </div>
                )}
              </aside>
            )}
          </div>
        )}
      </div>

      {!sidePanel && (
        <AppDrawer
          open={detailItem != null}
          onClose={() => setDetailId(null)}
          placement={phone ? 'bottom' : 'right'}
          size={phone ? '85%' : 400}
          title="판매처 목록"
          maskClosable
        >
          {panel}
        </AppDrawer>
      )}

      <AppModal
        open={bulkPlan != null}
        title="일괄 판매가 저장"
        onCancel={() => setBulkPlan(null)}
        okText={bulkPlan ? `${bulkPlan.targets.length}건 저장 · 네이버 반영` : '저장'}
        cancelText="취소"
        onOk={() => { if (bulkPlan) void runBulk(bulkPlan.targets); }}
      >
        {bulkPlan && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs, fontSize: FONT_SIZE.sm, color: t.textPrimary }}>
            <div>
              체크한 카드 <b>{bulkPlan.targets.length}건</b>의 판매가를 <b>{bulkPlan.price.toLocaleString('ko-KR')}원</b>으로 정해 네이버에 반영하고
              PriceHub 에 알립니다(PriceHub 확정 대기).
            </div>
            <div style={{ color: t.textSecondary }}>
              상승 <b style={{ color: t.success }}>{bulkPlan.up}건</b> · 하락 <b style={{ color: t.danger }}>{bulkPlan.down}건</b>
              {bulkPlan.noPrice > 0 && <> · 네이버 가격 없음 {bulkPlan.noPrice}건</>}
            </div>
            {(() => {
              // PriceHub 경고 카드(#362) — 막지 않고 이 확인 창에서 한 번 알린다.
              const ids = new Set(bulkPlan.targets.map((x) => x.cardId));
              const counts = new Map<string, number>();
              items.forEach((i) => {
                if (!ids.has(i.cardId)) return;
                (i.hubWarnings ?? []).forEach((w) => counts.set(w, (counts.get(w) ?? 0) + 1));
              });
              const warned = items.filter((i) => ids.has(i.cardId) && (i.hubWarnings?.length ?? 0) > 0).length;
              if (warned === 0) return null;
              return (
                <div role="alert" data-testid="bulk-hub-warnings" style={{ color: t.warning, fontSize: FONT_SIZE.xs }}>
                  PriceHub 경고 카드 {warned}건({Array.from(counts, ([w, n]) => `${hubWarningLabel(w)} ${n}`).join(' · ')}) — 그래도 결정합니다.
                </div>
              );
            })()}
            {(bulkPlan.excluded.notBelow + bulkPlan.excluded.same + bulkPlan.excluded.pending) > 0 && (
              <div style={{ color: t.textMuted, fontSize: FONT_SIZE.xs }}>
                제외 — {[
                  bulkPlan.excluded.notBelow ? `네이버 가격이 일괄 가격 이상 ${bulkPlan.excluded.notBelow}건(네이버 가격 미만만 적용)` : null,
                  bulkPlan.excluded.same ? `이미 같은 가격 ${bulkPlan.excluded.same}건` : null,
                  bulkPlan.excluded.pending ? `결과 불명 ${bulkPlan.excluded.pending}건(같은 요청으로 다시 시도)` : null,
                ].filter(Boolean).join(' · ')}
              </div>
            )}
            <div data-testid="bulk-hub-pending-note" style={{ color: t.textMuted, fontSize: FONT_SIZE.xs }}>
              PriceHub 확정 전까지 가격 조회 ③ · 자동 반영이 이 카드를 건드리지 않습니다 — 확정 대기는 매장 가격 요청에서 봅니다.
            </div>
            {bulkPlan.targets.length > MAX_DECIDED_BULK && (
              <div style={{ color: t.textMuted, fontSize: FONT_SIZE.xs }}>
                {MAX_DECIDED_BULK}건씩 나눠 보냅니다 — 한 묶음이 통째로 실패하면 거기서 멈춥니다.
              </div>
            )}
          </div>
        )}
      </AppModal>
      <AppModal
        open={warnTarget != null}
        title="PriceHub 경고 — 그래도 결정할까요?"
        onCancel={() => setWarnTarget(null)}
        okText="그래도 결정"
        cancelText="취소"
        onOk={() => {
          const id = warnTarget;
          setWarnTarget(null);
          if (id != null) void saveOne(id, { warned: true });
        }}
      >
        {warnTarget != null && (() => {
          const it = items.find((i) => i.cardId === warnTarget);
          return (
            <div data-testid="hub-warning-confirm" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs, fontSize: FONT_SIZE.sm, color: t.textPrimary }}>
              <div><b>{it?.productName ?? `카드 ${warnTarget}`}</b> — 결정은 막지 않지만 PriceHub 쪽 상태를 확인하세요.</div>
              {(it?.hubWarnings ?? []).map((w) => (
                <div key={w} style={{ color: t.warning }}><WarningOutlined aria-hidden /> {hubWarningLabel(w)} — {hubWarningHelp(w)}</div>
              ))}
            </div>
          );
        })()}
      </AppModal>

      <AppModal
        open={resetTarget != null}
        title="스토어 확인 후 새로 결정"
        onCancel={() => setResetTarget(null)}
        okText="스토어 확인함 · 새로 결정"
        cancelText="취소"
        onOk={confirmReset}
      >
        {resetTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs, fontSize: FONT_SIZE.sm, color: t.textPrimary }}>
            <div>
              지난 요청({resetTarget.price.toLocaleString('ko-KR')}원)이 네이버에 반영됐는지 알 수 없습니다. 같은 요청 번호로 다시 보내도
              서버는 같은 결과 불명을 돌려줍니다.
            </div>
            <div style={{ color: t.textSecondary }}>
              네이버 스토어에서 이 상품의 지금 판매가를 확인한 뒤 새로 결정하세요. 지난 요청은 버리고(다시 보내지 않음), 입력칸에 그 가격을
              다시 넣어 둡니다 — 저장하면 새 요청 번호로 보냅니다.
            </div>
          </div>
        )}
      </AppModal>

      <AppModal
        open={bulkProgress != null}
        title={bulkProgress?.running ? '판매가 결정 — 진행 중' : '판매가 결정 — 결과'}
        closable={!bulkProgress?.running}
        onCancel={() => { if (!bulkProgress?.running) setBulkProgress(null); }}
        footer={bulkProgress?.running ? null : [
          <AppButton key="close" variant="primary" onClick={() => setBulkProgress(null)}>닫기</AppButton>,
        ]}
      >
        {bulkProgress && (
          <div data-testid="decided-bulk-progress" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs, fontSize: FONT_SIZE.sm }}>
            <Progress
              percent={bulkProgress.total ? Math.round((bulkProgress.processed / bulkProgress.total) * 100) : 0}
              status={bulkProgress.running ? 'active' : bulkProgress.error ? 'exception' : 'normal'}
            />
            <div style={{ color: t.textSecondary }}>
              {bulkProgress.processed} / {bulkProgress.total}건
              {bulkProgress.parts > 1 && ` · 묶음 ${bulkProgress.part}/${bulkProgress.parts}`}
              {bulkProgress.running && bulkProgress.inFlight > 0 && ` · 처리 중 ${bulkProgress.inFlight}건`}
              {bulkProgress.running && bulkProgress.phase && ` · ${bulkProgress.phase}`}
            </div>
            {!bulkProgress.running && (
              <div>
                반영 <b style={{ color: t.success }}>{bulkProgress.tally.success}건</b>
                {' · '}실패 <b style={{ color: t.danger }}>{bulkProgress.tally.failed}건</b>
                {' · '}결과 불명 <b style={{ color: t.warning }}>{bulkProgress.tally.unknown}건</b>
                {bulkProgress.tally.notSent > 0 && <> · 보내지 않음 <b>{bulkProgress.tally.notSent}건</b></>}
              </div>
            )}
            {bulkProgress.error && <div role="alert" style={{ color: t.danger }}>{bulkProgress.error}</div>}
            {!bulkProgress.running && bulkProgress.tally.unknown > 0 && (
              <div style={{ color: t.textMuted, fontSize: FONT_SIZE.xs }}>
                결과 불명 카드는 위 노란 줄의 "같은 요청으로 다시 시도" 로만 다시 보냅니다(서버가 첫 결과를 돌려줘 두 번 반영되지 않습니다).
              </div>
            )}
            {!bulkProgress.running && bulkProgress.problems.length > 0 && (
              <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: SPACING.xxs }}>
                {bulkProgress.problems.map((pr) => {
                  const name = items.find((i) => i.cardId === pr.cardId)?.productName ?? `카드 ${pr.cardId}`;
                  const label = pr.kind === 'failed' ? '실패' : pr.kind === 'unknown' ? '결과 불명' : '보내지 않음';
                  return (
                    <div key={`${pr.cardId}-${pr.kind}`} style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
                      <b style={{ color: pr.kind === 'failed' ? t.danger : t.warning }}>{label}</b> {name}
                      {pr.message ? ` — ${pr.message}` : ''}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </AppModal>
    </DashboardLayout>
  );
};
